import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'

/** Material UI **/
import {
  CustomTextField,
  HighlightButton
} from '../../styles/mui_custom_component'
import {
  Box,
  Grid,
  InputAdornment,
  makeStyles,
  Icon,
  Typography
} from '@material-ui/core'
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'

/** Validations **/
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

/** Redux **/
import { useDispatch } from 'react-redux'
import { loadingActions } from '../../store/loading'

/** Services **/
import { login, resetPassword } from '../../services/AuthService'
import { handleSpace } from '../../lib/Global'

/** Images **/
import personalPadlockIcon from '../../assets/icons/personalPadlockIcon.svg'
import { appLogo } from '../../lib/Constants'
import Footer from './Footer'
import { ConfirmDialog } from '../../components/form/ConfirmDialog'

const useStyles = makeStyles(theme => ({
  mainGrid: {
    transform: 'scale(0.8)'
  },
  cornerIcon: {
    position: '',
    bottom: '0px',
    left: '0px',
    maxWidth: '30vw'
  },
  containerIconLogo: {
    position: 'absolute',
    left: '0px',
    bottom: '0px',
    [theme.breakpoints.down('sm')]: {
      left: 0,
      right: 0,
      marginleft: 'auto',
      marginright: 'auto'
    }
  },
  containerLogo: {
    position: 'absolute',
    left: '15px',
    top: '15px',
    [theme.breakpoints.down('sm')]: {
      top: '10px',
      left: 0,
      right: 0,
      marginleft: 'auto',
      marginright: 'auto'
    }
  },
  logo: {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    height: '50px',
    [theme.breakpoints.down('sm')]: {
      height: '50px'
    }
  },
  mainItem: {
    maxWidth: '30em',
    paddingTop: '4em'
  },
  icon: {
    width: '0.8em',
    position: 'relative',
    top: '-4px'
  },
  title: {
    fontWeight: '700',
    fontSize: '30px',
    color: theme.colors.black
  },
  subtitle: {
    marginTop: '30px',
    fontWeight: '300',
    fontSize: '20px',
    color: theme.colors.black
  },
  buttons: {
    marginTop: theme.spacing(4)
  },
  links: {
    paddingTop: '4px',
    fontWeight: '400',
    fontSize: '14px',
    color: theme.colors.black,
    textDecoration: 'none',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px'
    }
  }
}))

const ForgotPasswordCode = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const [newPassword, setNewPassword] = useState('')
  const [reEnterNewPassword, setReEnterNewPassword] = useState('')
  const [isValidPassword, setIsValidPassword] = useState()
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordReEnter, setShowPasswordReEnter] = useState(false)
  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const handleClickShowPasswordReEnter = () => setShowPasswordReEnter(!showPasswordReEnter)
  const handleMouseDownPassword = (event) => event.preventDefault()
  const [openDialog, setOpenDialog] = useState(false)

  /** VALIDATIONS **/
  const validationSchema = yup.object().shape({
    code: yup.string().required(t('general.messages.errors.required')),
    new_password: yup
      .string()
      .required(t('general.messages.errors.required'))
      .min(6, t('general.messages.errors.length_6')),
    re_enter_new_password: yup
      .string()
      .required(t('general.messages.errors.required'))
      .oneOf(
        [yup.ref('new_password'), null],
        t('general.messages.errors.password_does_not_match')
      )
  })

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    resetOptions: {
      keepErrors: false
    }
  })

  const onSubmit = async () => {
    try {
      dispatch(loadingActions.show())
      const queryParams = new URLSearchParams(window.location.search)
      const selector = queryParams.get('selector')
      const token = queryParams.get('token')
      const apiToken = queryParams.get('apiToken')
      const user = queryParams.get('u')
      const role = queryParams.get('role')
      await resetPassword(
        selector,
        token,
        newPassword,
        reEnterNewPassword,
        apiToken
      )
      if (role === 'mobile') {
        setOpenDialog(true)
      } else {
        await login(user, newPassword)
      }
      dispatch(loadingActions.hide())
    } catch (error) {
      /* eslint-disable no-console */
      console.error(error)
      dispatch(loadingActions.hide())
    }
  }

  const handleNewPasswordChange = event => {
    setNewPassword(event.target.value)
  }

  const handleReEnterNewPasswordChange = event => {
    setReEnterNewPassword(event.target.value)
  }

  useEffect(() => {
    // Password Validation
    if (newPassword !== reEnterNewPassword || newPassword.length < 6) {
      setIsValidPassword(false)
    } else {
      setIsValidPassword(true)
    }
  }, [newPassword, reEnterNewPassword])

  return (
    <div>
      <Grid className={classes.containerLogo} item xs={12}>
        <img
          alt={'MyWork'}
          className={classes.logo}
          src={appLogo}
        />
      </Grid>
      <Grid
        data-testid={'forgot_password_code_page'}
        className={classes.mainGrid}
        container
        spacing={0}
        direction="column"
        alignItems="center"
      >
        <Grid className={classes.mainItem} item xs={12}>
          <Grid container justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <Typography
                align="center"
                className={classes.title}
                component="h4"
                variant="h4"
              >
                {t('forgot_password_code.title')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                align="center"
                className={classes.subtitle}
                component="h4"
                variant="h4"
              >
                {t('forgot_password_code.subtitle_1')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box mt={4}>
                <form
                  className={classes.form}
                  noValidate
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <CustomTextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    id="new_password"
                    placeholder={t('forgot_password_code.new_password')}
                    name="new_password"
                    error={!!errors.new_password}
                    helperText={
                      errors.new_password && errors.new_password.message
                    }
                    {...register('new_password')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon>
                            <img
                              alt={'user'}
                              className={classes.icon}
                              src={personalPadlockIcon}
                            />
                          </Icon>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Icon
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </Icon>
                        </InputAdornment>
                      )
                    }}
                    onInput={handleNewPasswordChange}
                    onFocus={e => e.target.select()}
                    onKeyDown={e => handleSpace(e)}
                  /><br/><br/>
                  <CustomTextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    type={showPasswordReEnter ? 'text' : 'password'}
                    name="re_enter_new_password"
                    placeholder={t(
                      'forgot_password_code.re_enter_new_password'
                    )}
                    id="re_enter_new_password"
                    error={!!errors.re_enter_new_password}
                    helperText={
                      errors.re_enter_new_password &&
                      errors.re_enter_new_password.message
                    }
                    {...register('re_enter_new_password')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon>
                            <img
                              alt={'user'}
                              className={classes.icon}
                              src={personalPadlockIcon}
                            />
                          </Icon>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Icon
                            onClick={handleClickShowPasswordReEnter}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPasswordReEnter ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </Icon>
                        </InputAdornment>
                      )
                    }}
                    onInput={handleReEnterNewPasswordChange}
                    onFocus={e => e.target.select()}
                    onKeyDown={e => handleSpace(e)}
                  />

                  <Grid
                    className={classes.buttons}
                    container
                    justifyContent="flex-end"
                    spacing={3}
                  >
                    <Grid item>
                      <HighlightButton
                        data-testid={'submit_button'}
                        disabled={!isValidPassword}
                        type="submit"
                        variant="contained"
                        onClick={onSubmit}
                      >
                        {t('forgot_password_code.reset_password')}
                      </HighlightButton>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Footer />
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        title={t('forgot_password_code.mobile_title')}
        message={t('forgot_password_code.mobile_message')}
      />
    </div>
  )
}

export default ForgotPasswordCode
